import { validateTimesWithDetails } from '../../../util/CalendarUtil.js';
import { isClubOwner, isClubStaff } from '../../../util/UserRoles.js';

export const ALL_MEMBERS_ID = '__ALL_MEMBERS__';
export const DEFAULT_RSVP_MAX_SLOT = 999;

export const SCHEDULE_TYPE_CHOICES = [
    { id: 'TEAM_PRACTICE', label: 'Practice' },
    { id: 'EVENT', label: 'Game' },
    { id: 'MEETING', label: 'Meeting' },
];

export const schedulerEventColors = (scheduleType, resourceColor) => {
    if (resourceColor) {
        return { CategoryColor: resourceColor, PrimaryColor: 'transparent', SecondaryColor: resourceColor };
    }
    const type = String(scheduleType || '').toUpperCase();
    if (type === 'EVENT') {
        return { CategoryColor: '#1b3f6e', PrimaryColor: 'transparent', SecondaryColor: '#1b3f6e' };
    }
    if (type === 'MEETING') {
        return { CategoryColor: '#667085', PrimaryColor: 'transparent', SecondaryColor: '#667085' };
    }
    return { CategoryColor: '#92d841', PrimaryColor: 'transparent', SecondaryColor: '#92d841' };
};

export const isDarkEventColor = (hex = '') => {
    const value = String(hex).replace('#', '');
    if (value.length < 6) {
        return false;
    }
    const r = parseInt(value.slice(0, 2), 16);
    const g = parseInt(value.slice(2, 4), 16);
    const b = parseInt(value.slice(4, 6), 16);
    return ((r * 299) + (g * 587) + (b * 114)) / 1000 < 140;
};

export const toScheduleDate = (value) => {
    if (value instanceof Date && !Number.isNaN(value.getTime())) {
        return value;
    }
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? value : parsed;
};

export const eventDurationMinutes = (start, end) => {
    const startMs = new Date(start).getTime();
    const endMs = new Date(end).getTime();
    if (!Number.isFinite(startMs) || !Number.isFinite(endMs)) {
        return 60;
    }
    return Math.max(0, (endMs - startMs) / 60000);
};

export const isCompactSchedulerEvent = (start, end, currentView = 'Day') => (
    currentView === 'Month' || eventDurationMinutes(start, end) <= 30
);

export const calendarSlotInterval = (config) => {
    const n = Number(config?.interval);
    return n === 60 ? 60 : 30;
};

export const clubCalendarTimezone = (config) => {
    const zone = String(config?.calenderTimezone || config?.calendarTimezone || 'UTC').trim();
    return zone || 'UTC';
};

export const nowInClubTimezone = (config) => (
    new Date(new Date().toLocaleString('en-US', { timeZone: clubCalendarTimezone(config) }))
);

const hourToMinutes = (value) => {
    const [hours, minutes] = String(value || '00:00').split(':').map(Number);
    return ((Number.isFinite(hours) ? hours : 0) * 60) + (Number.isFinite(minutes) ? minutes : 0);
};

export const hoursFromCalendarConfig = (config, date = nowInClubTimezone(config)) => {
    const days = Array.isArray(config?.workingDays) ? config.workingDays : [];
    const safeDate = date instanceof Date && !Number.isNaN(date.getTime())
        ? date
        : nowInClubTimezone(config);
    const weekday = safeDate.getUTCDay();
    const match = days.find((item) => Number(item.day) === weekday);
    const open = match || days[0] || {};
    return {
        startHour: open.startHour || '08:00',
        endHour: open.endHour || '22:00',
    };
};

export const hoursForSchedulerView = (config, currentView, date) => {
    if (currentView !== 'Week' && currentView !== 'Month') {
        return hoursFromCalendarConfig(config, date);
    }
    const days = (Array.isArray(config?.workingDays) ? config.workingDays : [])
        .filter((item) => item && item.isOpen !== false && item.startHour && item.endHour);
    if (!days.length) {
        return hoursFromCalendarConfig(config, date);
    }
    return {
        startHour: days.reduce((earliest, item) => (
            hourToMinutes(item.startHour) < hourToMinutes(earliest) ? item.startHour : earliest
        ), days[0].startHour),
        endHour: days.reduce((latest, item) => (
            hourToMinutes(item.endHour) > hourToMinutes(latest) ? item.endHour : latest
        ), days[0].endHour),
    };
};

export const parseMeetingData = (meetingData) => {
    if (!meetingData) {
        return { mode: '', platform: '' };
    }
    let parsed = meetingData;
    if (typeof meetingData === 'string') {
        try {
            parsed = JSON.parse(meetingData);
        } catch {
            return { mode: '', platform: '' };
        }
    }
    return {
        mode: parsed?.mode ?? '',
        platform: parsed?.platform ?? '',
    };
};

export const canAttachAllSchedulerMembers = (calenderConfig, userInfo) => (
    !calenderConfig?.coachPrivacy || isClubOwner(userInfo) || isClubStaff(userInfo)
);

export const resolveRsvpMaxSlot = (isRSVP, existingMaxSlot) => {
    if (!isRSVP) {
        return 0;
    }
    const n = Number(existingMaxSlot);
    return Number.isFinite(n) && n > 0 ? n : DEFAULT_RSVP_MAX_SLOT;
};

export const normalizeTeamIds = (teamIds) => {
    if (typeof teamIds === 'string') {
        const trimmed = teamIds.trim();
        if (!trimmed) {
            return [];
        }
        try {
            return normalizeTeamIds(JSON.parse(trimmed));
        } catch {
            return trimmed === ALL_MEMBERS_ID ? [] : [trimmed];
        }
    }
    if (!Array.isArray(teamIds)) {
        return [];
    }
    const seen = new Set();
    return teamIds
        .map((item) => (item && typeof item === 'object' ? item.id : item))
        .filter((id) => {
            if (!id || id === ALL_MEMBERS_ID || seen.has(id)) {
                return false;
            }
            seen.add(id);
            return true;
        });
};

export const allMembersWhoOption = () => ({ id: ALL_MEMBERS_ID, name: 'All members' });

export const uniqueWhoOptions = (items = []) => {
    const seen = new Set();
    return items.filter((item) => {
        const id = item?.id;
        if (id == null || id === '') {
            return false;
        }
        const key = String(id);
        if (seen.has(key)) {
            return false;
        }
        seen.add(key);
        return true;
    });
};

export const toWhoOption = (item) => {
    if (!item || typeof item !== 'object') {
        return null;
    }
    if (item.id === ALL_MEMBERS_ID) {
        return allMembersWhoOption();
    }
    const id = item.id || item.playerId || item.userId || item.teamId || item.memberId;
    const name = item.name
        || item.playerName
        || item.fullName
        || item.userName
        || item.displayName
        || item.teamName
        || [item.firstName || item.userFirstName || item.first_name, item.lastName || item.userLastName || item.last_name].filter(Boolean).join(' ').trim()
        || item.email
        || item.userEmail
        || (id != null ? String(id) : '');
    if (id == null || id === '' || !name) {
        return null;
    }
    const isTeam = Boolean(item.team || item.teamId || item.teamName);
    return {
        id,
        name,
        email: item.email || item.userEmail || (isTeam ? 'Team' : ''),
        logo: item.logo || item.profileLogo || item.profileImage || item.teamLogo,
        team: isTeam,
    };
};

export const catalogWhoOptions = (players = [], teams = []) => uniqueWhoOptions([
    ...(Array.isArray(players) ? players : []).map(toWhoOption),
    ...(Array.isArray(teams) ? teams : []).map((team) => toWhoOption({ ...team, team: true })),
].filter(Boolean));

export const filterWhoCatalog = (catalog = [], query = '') => {
    const needle = String(query || '').trim().toLowerCase();
    if (needle.length < 3) {
        return [];
    }
    return catalog.filter((item) => {
        const name = String(item?.name || '').toLowerCase();
        const email = String(item?.email || '').toLowerCase();
        return name.includes(needle) || email.includes(needle);
    });
};

export const playersFromSchedulePayload = (data) => {
    if (Array.isArray(data)) {
        return data;
    }
    if (!data || typeof data !== 'object') {
        return [];
    }
    const nested = data.players || data.playerList || data.users || data.members || data.Data;
    return Array.isArray(nested) ? nested : [];
};

export const recordsFromWhoSearch = (data) => {
    if (Array.isArray(data)) {
        return data;
    }
    if (!data || typeof data !== 'object') {
        return [];
    }
    if (data.id || data.userId || data.playerId) {
        return [data];
    }
    const nested = data.players
        || data.users
        || data.members
        || data.content
        || data.list
        || data.records
        || data.items
        || data.result
        || data.Data;
    if (Array.isArray(nested)) {
        return nested;
    }
    if (nested && typeof nested === 'object') {
        return recordsFromWhoSearch(nested);
    }
    return [];
};

export const resolveWhoOptions = (ids = [], ...lists) => {
    const wanted = new Set(normalizeTeamIds(ids).map(String));
    const pool = uniqueWhoOptions(lists.flat().map(toWhoOption).filter(Boolean));
    const named = pool.filter((item) => wanted.has(String(item.id)));
    const missing = normalizeTeamIds(ids).filter((id) => !named.some((item) => String(item.id) === String(id)));
    return uniqueWhoOptions([
        ...named,
        ...missing.map((id) => ({ id, name: String(id), email: '', logo: undefined, team: false })),
    ]);
};

export const searchSchedulerWho = async ({
    query,
    clubId,
    catalog = [],
    selected = [],
    canAttachAll = false,
    searchClubUsers,
}) => {
    const selectedOptions = uniqueWhoOptions((selected || []).map(toWhoOption).filter(Boolean));
    const allOption = canAttachAll ? [allMembersWhoOption()] : [];
    const needle = String(query || '').trim();
    if (needle.length < 3) {
        return uniqueWhoOptions([...allOption, ...selectedOptions]);
    }
    let remote = [];
    if (clubId && typeof searchClubUsers === 'function') {
        const found = await searchClubUsers(clubId, needle);
        remote = recordsFromWhoSearch(found).map(toWhoOption).filter(Boolean);
    }
    return uniqueWhoOptions([
        ...allOption,
        ...selectedOptions,
        ...filterWhoCatalog(catalog, needle),
        ...remote,
    ]);
};

export const readSchedulerWhoList = (root) => {
    const fromMulti = root?.querySelector('#PlayerAndTeam')?.ej2_instances?.[0]?.listData;
    if (Array.isArray(fromMulti) && fromMulti.length) {
        return fromMulti.map(toWhoOption).filter(Boolean);
    }
    const raw = root?.querySelector('#PlayerAndTeamList')?.value;
    if (!raw) {
        return [];
    }
    try {
        return JSON.parse(raw).map(toWhoOption).filter(Boolean);
    } catch {
        return [];
    }
};

export const readFormChecked = (root, selector) => {
    const el = root?.querySelector(selector);
    return Boolean(el?.ej2_instances?.[0]?.checked ?? el?.checked);
};

export const readFormValue = (root, selector) => {
    const el = root?.querySelector(selector);
    return el?.ej2_instances?.[0]?.value ?? el?.value;
};

export const seriesIdOf = (data = {}) =>
    data?.SeriesId ?? data?.seriesId ?? data?.RecurrenceID ?? data?.recurrenceId ?? null;

export const recurrenceIdOf = (data = {}) => seriesIdOf(data);

export const isUpdateEntireSeries = (data = {}) => data.editEntireSeries === true;

export const normalizeRecurrenceRule = (value) => {
    if (value == null) {
        return null;
    }
    let rule = String(value).trim();
    if (!rule || /^none$/i.test(rule)) {
        return null;
    }
    if (rule.toUpperCase().startsWith('RRULE:')) {
        rule = rule.slice(6).trim();
    }
    if (!/FREQ=/i.test(rule)) {
        return null;
    }
    if (!/COUNT=/i.test(rule) && !/UNTIL=/i.test(rule)) {
        rule = `${rule.replace(/;+$/, '')};COUNT=10`;
    }
    return rule;
};

export const readRecurrenceRule = (root, fallback) => (
    normalizeRecurrenceRule(
        readFormValue(root, '#RecurrenceRule')
        || root?.querySelector('#coach-recurrence-editor')?.ej2_instances?.[0]?.getRecurrenceRule?.()
        || readFormValue(root, '#coach-recurrence-editor')
        || fallback
    )
);

export const validateSchedulerEditor = (root) => {
    const subject = readFormValue(root, '#title');
    if (!String(subject ?? '').trim()) {
        return 'Title is required.';
    }
    if (String(subject).length > 255) {
        return 'Title cannot be greater than 255 character.';
    }

    const timeError = validateTimesWithDetails(
        toScheduleDate(readFormValue(root, '#StartTime')),
        toScheduleDate(readFormValue(root, '#EndTime'))
    );
    if (timeError) {
        return timeError;
    }

    const scheduleType = readFormValue(root, '#schedule_type');
    const isAttachAllMember = readFormChecked(root, '#is_attach_all_member');
    const teamIds = normalizeTeamIds(
        readFormValue(root, '#PlayerAndTeam') || readSchedulerWhoList(root).map((item) => item.id)
    );
    const venue = root?.querySelector('#Venue')?.value ?? '';
    const desc = root?.querySelector('#Description')?.value ?? '';
    const meetingMode = readFormValue(root, '#MeetingMode');
    const platform = readFormValue(root, '#Platform');

    if (!isAttachAllMember && teamIds.length === 0) {
        return 'Choose All members or at least one player.';
    }
    if (scheduleType === 'MEETING' && !meetingMode) {
        return 'Meeting mode is required.';
    }
    if (!isOnlineMeeting(scheduleType, meetingMode) && !String(venue).trim()) {
        return 'Location is required.';
    }
    if (isOnlineMeeting(scheduleType, meetingMode) && !platform) {
        return 'Meeting platform is required.';
    }
    if (String(venue).length > 300) {
        return 'Location cannot be greater than 300 character.';
    }
    if (String(desc).length > 5000) {
        return 'Description cannot be greater than 5000 character.';
    }
    return '';
};

export const isOnlineMeeting = (scheduleType, meetingMode) => (
    scheduleType === 'MEETING' && meetingMode === 'ONLINE'
);

export const typeChoiceForUi = (scheduleType) => (
    scheduleType === 'MEET' ? 'TEAM_PRACTICE' : scheduleType
);

export const hideDefaultScheduleEditorFields = (root) => {
    if (!root?.querySelectorAll) {
        return;
    }
    [
        '#Timezone',
        '#Location',
        '.e-location-container',
        '.e-time-zone',
        '.e-timezone',
        '.e-time-zone-container',
        '.e-all-day-time-zone-row',
    ].forEach((selector) => {
        root.querySelectorAll(selector).forEach((el) => {
            const row = el.closest('tr') || el;
            row.style.display = 'none';
        });
    });
    root.querySelectorAll('.e-textlabel, label, .e-float-text').forEach((el) => {
        if (String(el.textContent || '').trim().toLowerCase() !== 'timezone') {
            return;
        }
        const row = el.closest('tr') || el.parentElement;
        if (row) {
            row.style.display = 'none';
        }
    });
};

export const hydrateSchedulerEvent = (data = {}) => ({
    IsRSVP: Boolean(data.rsvp ?? data.isRSVP ?? data.IsRSVP),
    IsAttachAllMember: Boolean(data.isAttachAllMember ?? data.attachAllMember ?? data.IsAttachAllMember),
    IsAttachAllGuest: Boolean(data.isAttachAllGuest ?? data.attachAllGuest ?? data.IsAttachAllGuest),
    IsAwayOrgRegistered: Boolean(data.awayOrgId ?? data.AwayOrgId ?? data.isAwayOrgRegistered ?? data.IsAwayOrgRegistered),
    TeamIds: normalizeTeamIds(data.teamIds ?? data.TeamIds),
    MeetingData: parseMeetingData(data.meetingData ?? data.MeetingData),
});
